import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, ClipboardCheck, KeyRound, LogOut, Menu, Search, X, Languages, Check, Landmark, PiggyBank, Eye, SlidersHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useT, LANGS } from "@/i18n";
import { GlobalSearchDialog } from "@/components/GlobalSearch";
import { PayrollAIChat } from "@/components/PayrollAIChat";
import { PoweredBy } from "@/components/PoweredBy";
import { TID } from "@/constants/testIds";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const mainLinks = [
  { to: "/", key: "nav.dashboard", tid: TID.nav.dashboard, mtid: TID.nav.mobileDashboard },
  { to: "/payroll", key: "nav.payroll", tid: "nav-payroll-link", mtid: "nav-mobile-payroll-link" },
  { to: "/input-payroll", key: "nav.inputPayroll", tid: "nav-input-payroll-link", mtid: "nav-mobile-input-payroll-link" },
  { to: "/karyawan", key: "nav.employees", tid: TID.nav.employees, mtid: TID.nav.mobileEmployees },
  { to: "/kasbon", key: "nav.kasbon", tid: "nav-kasbon-link", mtid: "nav-mobile-kasbon-link" },
  { to: "/absensi", key: "nav.attendance", tid: "nav-absensi-link", mtid: "nav-mobile-absensi-link" },
  { to: "/jarit", key: "nav.tailors", tid: TID.nav.jarit, mtid: TID.nav.mobileJarit },
];

const entryLinks = [
  { to: "/input-payroll", key: "nav.inputPayroll", tid: "nav-input-payroll-link", mtid: "nav-mobile-input-payroll-link" },
  { to: "/jarit", key: "nav.tailors", tid: TID.nav.jarit, mtid: TID.nav.mobileJarit },
];

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang, applyUserDefault } = useT();
  useEffect(() => {
    if (user?.default_lang) applyUserDefault(user.default_lang);
  }, [user?.default_lang, applyUserDefault]);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "viewer";
  const isViewer = user?.role === "viewer";

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navClass = ({ isActive }) =>
    `rounded-full px-3.5 py-1.5 text-[13.5px] font-medium transition-colors ${
      isActive
        ? "bg-black/[0.06] text-[#1D1D1F]"
        : "text-[#6E6E73] hover:bg-black/[0.03] hover:text-[#1D1D1F]"
    }`;

  const mobileNavClass = ({ isActive }) =>
    `block rounded-xl px-3 py-2.5 text-[15px] font-medium ${
      isActive ? "bg-black/[0.05] text-[#1D1D1F]" : "text-[#6E6E73]"
    }`;

  // Apple-style liquid glass pill surface
  const glass =
    "border border-white/40 bg-white/75 sm:bg-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.10)] ring-1 ring-inset ring-white/40 backdrop-blur-2xl backdrop-saturate-150 supports-[backdrop-filter]:bg-white/65 sm:supports-[backdrop-filter]:bg-white/15";

  return (
    <div className="app-bg min-h-screen">
      <div className="app-mobile-scale">
      <header className="sticky top-2 z-40 mx-3 mt-2 sm:mx-4 sm:top-3 sm:mt-3">
        <div className={`mx-auto flex h-14 items-center gap-3 rounded-full px-4 sm:gap-6 sm:px-8 ${glass}`}>
          <Link to="/" className="flex min-w-0 items-center gap-2.5">
            <img src="/meranggi-logo.png" alt="Meranggi" className="h-8 w-8 shrink-0 rounded-full object-cover" />
            <span className="truncate text-[14px] font-semibold tracking-tight sm:text-[15px]">CV. MERANGGI JAYA</span>
          </Link>

          <nav className="ml-2 hidden items-center gap-1 md:flex">
            {isAdmin ? (
              <>
                <NavLink to="/" end data-testid={TID.nav.dashboard} className={navClass}>
                  {t("nav.dashboard")}
                </NavLink>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-payroll-menu"
                      className="flex items-center gap-1 rounded-full px-3.5 py-1.5 text-[13.5px] font-medium text-[#6E6E73] transition-colors hover:bg-black/[0.03] hover:text-[#1D1D1F]"
                    >
                      {t("nav.payroll")} <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52 rounded-2xl border-white/50 bg-white/75 shadow-[0_20px_50px_rgba(0,0,0,0.18)] backdrop-blur-2xl backdrop-saturate-150">
                    <DropdownMenuItem data-testid="menu-nav-payroll-link" onClick={() => navigate("/payroll")}>
                      {t("nav.payroll")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-nav-input-payroll-link" onClick={() => navigate("/input-payroll")}>
                      {t("nav.inputPayroll")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <NavLink to="/karyawan" data-testid={TID.nav.employees} className={navClass}>
                  {t("nav.employees")}
                </NavLink>
                <NavLink to="/kasbon" data-testid="nav-kasbon-link" className={navClass}>
                  {t("nav.kasbon")}
                </NavLink>
                <NavLink to="/absensi" data-testid="nav-absensi-link" className={navClass}>
                  {t("nav.attendance")}
                </NavLink>
                <NavLink to="/jarit" data-testid={TID.nav.jarit} className={navClass}>
                  {t("nav.tailors")}
                </NavLink>
              </>
            ) : (
              <>
                <NavLink to="/" end data-testid="nav-beranda-link" className={navClass}>
                  {t("nav.home")}
                </NavLink>
                <NavLink to="/karyawan" data-testid="nav-karyawan-link" className={navClass}>
                  {t("nav.employees")}
                </NavLink>
                {entryLinks.map((l) => (
                  <NavLink key={l.to} to={l.to} data-testid={l.tid} className={navClass}>
                    {t(l.key)}
                  </NavLink>
                ))}
                <NavLink to="/kasbon" data-testid="nav-kasbon-link" className={navClass}>
                  {t("nav.kasbon")}
                </NavLink>
                <NavLink to="/absensi" data-testid="nav-absensi-link" className={navClass}>
                  {t("nav.attendance")}
                </NavLink>
              </>
            )}
          </nav>

          <div className="ml-auto hidden items-center gap-3 md:flex">
            {isViewer && (
              <span data-testid="viewer-badge" className="flex items-center gap-1.5 rounded-full bg-[#EAF2FE] px-3 py-1 text-[12px] font-semibold text-[#0071E3]">
                <Eye className="h-3.5 w-3.5" /> {t("nav.viewOnly")}
              </span>
            )}
            <button
              data-testid="global-search-button"
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13.5px] font-medium text-[#6E6E73] transition-colors hover:bg-black/[0.04] hover:text-[#1D1D1F]"
            >
              <Search className="h-4 w-4" /> {t("nav.search")}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={TID.account.navLink}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13.5px] font-medium text-[#6E6E73] transition-colors hover:bg-black/[0.04] hover:text-[#1D1D1F]"
                >
                  {user?.username} <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-2xl border-white/50 bg-white/75 shadow-[0_20px_50px_rgba(0,0,0,0.18)] backdrop-blur-2xl backdrop-saturate-150">
                {isAdmin ? (
                  <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
                    <SlidersHorizontal className="mr-2 h-4 w-4" /> {t("nav.settings")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="menu-account-center" onClick={() => navigate("/account")}>
                    <KeyRound className="mr-2 h-4 w-4" /> {t("nav.account")}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem data-testid={TID.nav.titip} onClick={() => navigate("/saldo-titip")}>
                    <PiggyBank className="mr-2 h-4 w-4" /> {t("nav.savings")}
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem data-testid="menu-approvals" onClick={() => navigate("/approvals")}>
                    <ClipboardCheck className="mr-2 h-4 w-4" /> {t("nav.approvals")}
                  </DropdownMenuItem>
                )}
                <div className="my-1 h-px bg-black/[0.06]" />
                <DropdownMenuLabel className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#8E8E93]">
                  <Languages className="h-3.5 w-3.5" /> {t("nav.language")}
                </DropdownMenuLabel>
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} data-testid={`menu-lang-${l.code}`} onClick={() => setLang(l.code)}>
                    <span className="flex-1">{l.label}</span>
                    {lang === l.code && <Check className="h-4 w-4 text-[#0071E3]" />}
                  </DropdownMenuItem>
                ))}
                <div className="my-1 h-px bg-black/[0.06]" />
                <DropdownMenuItem data-testid={TID.nav.logout} onClick={doLogout}>
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <button
            data-testid="global-search-mobile-button"
            onClick={() => setSearchOpen(true)}
            className="ml-auto rounded-full p-2 text-[#1D1D1F] transition-colors hover:bg-black/[0.05] md:hidden"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </button>
          <button
            data-testid={TID.nav.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="rounded-full p-2 text-[#1D1D1F] transition-colors hover:bg-black/[0.05] md:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-4.5 w-4.5" /> : <Menu className="h-4.5 w-4.5" />}
          </button>
        </div>

        {open && (
          <div
            data-testid={TID.nav.mobileMenu}
            className={`mx-auto mt-2 rounded-2xl p-3 md:hidden ${glass}`}
          >
            {isAdmin &&
              mainLinks.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={l.mtid} end={l.to === "/"} onClick={() => setOpen(false)} className={mobileNavClass}>
                  {t(l.key)}
                </NavLink>
              ))}
            {!isAdmin && (
              <NavLink to="/" end data-testid="nav-mobile-beranda-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                {t("nav.home")}
              </NavLink>
            )}
            {!isAdmin && (
              <NavLink to="/karyawan" data-testid="nav-mobile-karyawan-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                {t("nav.employees")}
              </NavLink>
            )}
            {!isAdmin && entryLinks.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={l.mtid} onClick={() => setOpen(false)} className={mobileNavClass}>
                {t(l.key)}
              </NavLink>
            ))}
            {!isAdmin && (
              <NavLink to="/kasbon" data-testid="nav-mobile-kasbon-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                {t("nav.kasbon")}
              </NavLink>
            )}
            {!isAdmin && (
              <NavLink to="/absensi" data-testid="nav-mobile-absensi-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                {t("nav.attendance")}
              </NavLink>
            )}
            {isAdmin && (
              <>
                <div className="my-2 h-px bg-black/[0.06]" />
                <NavLink to="/settings" data-testid="nav-mobile-settings-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                  <span className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> {t("nav.settings")}</span>
                </NavLink>
                <NavLink to="/saldo-titip" data-testid={TID.nav.mobileTitip} onClick={() => setOpen(false)} className={mobileNavClass}>
                  <span className="flex items-center gap-2"><PiggyBank className="h-4 w-4" /> {t("nav.savings")}</span>
                </NavLink>
                <NavLink to="/approvals" data-testid="nav-mobile-approvals-link" onClick={() => setOpen(false)} className={mobileNavClass}>
                  <span className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> {t("nav.approvals")}</span>
                </NavLink>
              </>
            )}
            <div className="my-2 h-px bg-black/[0.06]" />
            <NavLink to="/account" data-testid={TID.account.mobileNavLink} onClick={() => setOpen(false)} className={mobileNavClass}>
              {t("nav.account")}
            </NavLink>
            <div className="my-2 h-px bg-black/[0.06]" />
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8E8E93]">{t("nav.language")}</p>
            <div className="flex flex-wrap gap-2 px-3 pb-2">
              {LANGS.map((l) => (
                <button key={l.code} data-testid={`mobile-lang-${l.code}`} onClick={() => setLang(l.code)}
                  className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors ${lang === l.code ? "bg-[#0071E3] text-white" : "bg-black/[0.05] text-[#6E6E73]"}`}>
                  {l.short}
                </button>
              ))}
            </div>
            <button
              data-testid={TID.nav.mobileLogout}
              onClick={doLogout}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-[15px] font-medium text-[#FF3B30]"
            >
              <LogOut className="h-4 w-4" /> {t("nav.logout")}
            </button>
          </div>
        )}
      </header>

      <main className="mx-auto px-4 pt-6 pb-28 sm:px-8 sm:pt-10 sm:pb-32">{children}</main>
      <PoweredBy />
      <GlobalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <PayrollAIChat />
      </div>
    </div>
  );
};

export default AppShell;
